import { IconContentFormatBold } from '@doltech/icons/IconContentFormatBold';
import { IconContentFormatItalic } from '@doltech/icons/IconContentFormatItalic';
import { IconContentFormatUnderline } from '@doltech/icons/IconContentFormatUnderline';
import { IconContentLink } from '@doltech/icons/IconContentLink';
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import cl from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { ToolbarMark } from '../../../../../../slatePlugins/components/ToolbarMark/ToolbarMark';
import { MARK_BOLD } from '../../../../../../slatePlugins/marks/bold/defaults';
import { MARK_ITALIC } from '../../../../../../slatePlugins/marks/italic/defaults';
import { MARK_UNDERLINE } from '../../../../../../slatePlugins/marks/underline/defaults';
import { useInputCaptionPlugin } from '../../../../../BlogEditor/TextOnlyEditorPlugin.presets';
import { colorsV2 } from '../../../../../colors-v2';
import { DolEditor } from '../../../../DolEditor';
import { DolBalloonToolbar } from '../../../../toolbars/BalloonToolbar';
import { ToolbarHyperLink } from '../../../hyper-link/toolbar/ToolbarHyperLink';
import { ToolbarHyperLinkContextAction } from '../../../hyper-link/toolbar/ToolbarHyperLinkContextAction';
import { useFileUploadStore } from '../../model/file-upload-model';

interface Props {
  id?: string;
  onUpdateCaption?: (value: string) => void;
  caption?: string;
  isReadOnly?: boolean;
  withoutStore?: boolean;
}

export const Wrapper = styled.div`
  &&& {
    .dol-editor {
      min-height: 20px;
      padding: 0 !important;
      font-family: Inter, sans-serif !important;

      .not-preview {
        color: ${colorsV2.black70};
      }
    }

    .custom-toolbar {
      background: ${colorsV2.white100};
      border: unset;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.04),
      0 0 1px rgba(0, 0, 0, 0.04);
      border-radius: 8px;
    }
  }
`;

export const InputCaptionComponent = ({
                                        id = undefined,
                                        onUpdateCaption = noop,
                                        caption = undefined,
                                        isReadOnly = false,
                                        withoutStore,
                                      }: Props) => {
  const fileUploadStore = useFileUploadStore();
  const { plugins, editorChains } = useInputCaptionPlugin({ isReadOnly });

  const imageCaption = React.useMemo(() => {
    let result;
    try {
      result = caption ? JSON.parse(caption) : JSON.parse(fileUploadStore.getFileById(id)?.caption);
    } catch (ex) {
      result = [getEmptyParagraphNode('')];
    }

    return result;
  }, [caption, fileUploadStore, id]);
  const updateEditorValue = (value) => {
    if (withoutStore) {
      onUpdateCaption(JSON.stringify(value));
    } else {
      fileUploadStore.updateCaption(id, JSON.stringify(value));
    }
  };

  return (
    <Wrapper className="input-caption-wrapper">
      <DolEditor
        readOnly={isReadOnly}
        plugins={plugins}
        className={cl('caption-of-picture', { 'not-preview': isReadOnly })}
        placeholder={isReadOnly ? '' : 'Caption of Picture'}
        initialValue={imageCaption}
        editorChains={editorChains}
        withToolbar={
          !isReadOnly ? (
            <>
              <DolBalloonToolbar.OnExpanded
                className="custom-toolbar"
                direction="bottom"
                theme="light"
              >
                <ToolbarMark
                  reversed
                  type={MARK_BOLD}
                  icon={<IconContentFormatBold/>}
                  tooltip={{ content: 'Bold (⌘B)' }}
                />
                <ToolbarMark
                  reversed
                  type={MARK_ITALIC}
                  icon={<IconContentFormatItalic/>}
                  tooltip={{ content: 'Italic (⌘+I)' }}
                />
                <ToolbarMark
                  reversed
                  type={MARK_UNDERLINE}
                  icon={<IconContentFormatUnderline/>}
                  tooltip={{ content: 'Underline (⌘+U)' }}
                />
                <ToolbarHyperLink icon={<IconContentLink/>}/>
              </DolBalloonToolbar.OnExpanded>
              <ToolbarHyperLinkContextAction/>
            </>
          ) : undefined
        }
        onEditorChange={(value) => {
          updateEditorValue(value);
        }}
      />
    </Wrapper>
  );
};
